import { SEO } from "@/components/SEO";
import { BackButton } from "@/components/BackButton";
import { Shield, AlertTriangle, Ban, FileCheck } from "lucide-react";

const CSAEPolicy = () => {
  return (
    <>
      <SEO 
        title="Anti-CSAE Policy - Child Safety Protection"
        description="Our comprehensive Anti-Child Sexual Abuse and Exploitation (CSAE) policy. Learn about our zero-tolerance approach to protecting children on our platform."
        url="/csae-policy"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 md:py-12 max-w-4xl">
          <BackButton />
          
          <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8 md:p-10 mt-6">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                Anti-Child Sexual Abuse and Exploitation Policy
              </h1>
            </div>
            
            <div className="space-y-6 sm:space-y-8 text-foreground/90">
              <section>
                <p className="text-base sm:text-lg leading-relaxed">
                  <strong>Last Updated:</strong> January 2025
                </p>
                <p className="text-base sm:text-lg leading-relaxed mt-4">
                  At HiStage, the safety of children is our highest priority. We maintain a zero-tolerance policy 
                  toward child sexual abuse and exploitation (CSAE) in any form on our platform.
                </p>
              </section>

              <section className="bg-destructive/10 border-l-4 border-destructive p-4 sm:p-6 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-6 w-6 text-destructive flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-2">Zero Tolerance</h2>
                    <p className="text-sm sm:text-base">
                      We prohibit all content and conduct that sexually exploits or endangers children. 
                      Violations result in immediate account termination and reporting to law enforcement.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Ban className="h-6 w-6 text-primary" />
                  <h2 className="text-xl sm:text-2xl font-semibold text-foreground">Prohibited Content</h2>
                </div>
                <p className="text-base sm:text-lg leading-relaxed mb-4">
                  The following content is strictly prohibited on HiStage:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base">
                  <li>Child Sexual Abuse Material (CSAM) in any form</li>
                  <li>Content depicting, encouraging, or promoting the sexual exploitation of children</li>
                  <li>Sexually explicit or suggestive content involving minors</li>
                  <li>Content that sexualizes children, including animated or digital depictions</li>
                  <li>Grooming behavior or solicitation of minors</li>
                  <li>Content facilitating or coordinating child exploitation</li>
                  <li>Fantasy content depicting minors in sexual scenarios</li>
                  <li>Content that normalizes, glorifies, or trivializes child abuse</li>
                </ul>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                  <h2 className="text-xl sm:text-2xl font-semibold text-foreground">Our Commitment</h2>
                </div>
                <div className="space-y-4 text-sm sm:text-base">
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Proactive Detection</h3>
                    <p>We employ advanced technology and human review to detect and remove CSAE content before it spreads.</p>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Law Enforcement Cooperation</h3>
                    <p>We report all instances of CSAE to the National Center for Missing & Exploited Children (NCMEC) 
                    and cooperate fully with law enforcement agencies worldwide.</p>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Immediate Action</h3>
                    <p>Accounts violating this policy are permanently banned without warning. We preserve evidence 
                    for law enforcement investigation.</p>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Content Moderation</h3>
                    <p>Our dedicated trust and safety team reviews flagged content 24/7, prioritizing reports 
                    involving potential child exploitation.</p>
                  </div>
                </div>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-4">
                  <FileCheck className="h-6 w-6 text-primary" />
                  <h2 className="text-xl sm:text-2xl font-semibold text-foreground">Reporting</h2>
                </div>
                <p className="text-base sm:text-lg leading-relaxed mb-4">
                  If you encounter content or behavior that exploits or endangers children:
                </p>
                <div className="bg-primary/10 p-4 sm:p-6 rounded-lg space-y-3">
                  <p className="font-semibold text-base sm:text-lg">Report it immediately:</p>
                  <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base">
                    <li>Use the in-app report button on any content or profile</li>
                    <li>Email our Trust & Safety team: <a href="mailto:safety@histage.com" className="text-primary hover:underline">safety@histage.com</a></li>
                    <li>For urgent concerns, contact NCMEC directly: <a href="https://report.cybertip.org/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">CyberTipline</a></li>
                  </ul>
                  <p className="text-sm italic mt-4">
                    Your reports help us keep children safe. All reports are reviewed promptly and treated with 
                    the highest priority.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-4">Age Verification</h2>
                <p className="text-base sm:text-lg leading-relaxed">
                  All users must be at least 18 years old to create an account or access our platform. 
                  We employ age verification measures and remove accounts belonging to minors.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-4">Ongoing Improvements</h2>
                <p className="text-base sm:text-lg leading-relaxed">
                  We continuously enhance our child safety measures by investing in new technologies, 
                  partnering with child safety organizations, and training our teams on emerging threats 
                  and best practices.
                </p>
              </section>

              <section className="border-t pt-6 mt-8">
                <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-4">Resources</h2>
                <ul className="space-y-2 text-sm sm:text-base">
                  <li>
                    <a href="https://www.missingkids.org/gethelpnow/cybertipline" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      National Center for Missing & Exploited Children (NCMEC)
                    </a>
                  </li>
                  <li>
                    <a href="https://www.internetwatch.org.uk/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Internet Watch Foundation (IWF)
                    </a>
                  </li>
                  <li>
                    <a href="https://www.weprotect.org/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      WeProtect Global Alliance
                    </a>
                  </li>
                </ul>
              </section>

              <section className="bg-muted/30 p-4 sm:p-6 rounded-lg">
                <p className="text-sm sm:text-base italic">
                  This policy applies to all users, content, and activities on HiStage. We reserve the right 
                  to update this policy as needed to strengthen child protection measures.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CSAEPolicy;
